import { useCallback, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FileTree } from '../components/file-tree.js';
import { FileViewer } from '../components/file-viewer.js';
import { GitPanel } from '../components/git-panel.js';

const MD_BREAKPOINT = 768;

function subscribe(cb: () => void) {
  const mql = window.matchMedia(`(min-width: ${MD_BREAKPOINT}px)`);
  mql.addEventListener('change', cb);
  return () => mql.removeEventListener('change', cb);
}

function getSnapshot() {
  return window.matchMedia(`(min-width: ${MD_BREAKPOINT}px)`).matches;
}

function getServerSnapshot() {
  return true; // SSR defaults to desktop
}

function useIsDesktop() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

const frame = { border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)' };
// The rest of the viewport: 17rem is the app's top padding plus the server page's header (back link, name, status
// line) and tabs; to update if those grow. Never so short that the tree is unusable.
const height = 'h-[calc(100dvh-17rem)] min-h-[26rem]';

/**
 * The workspace's files, as a tab of the server page: under the server's header and tabs, like the other tabs. On a
 * phone, two levels: the tree (git below it), then the file opened full width with a way back to the tree.
 */
export function FileExplorer({ serverId }: { serverId: string }) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedPath = searchParams.get('path');
  const selectedType = (searchParams.get('type') as 'file' | 'directory') || 'file';
  const isDesktop = useIsDesktop();

  const handleSelect = useCallback((path: string, type: 'file' | 'directory') => {
    setSearchParams({ path, type }, { replace: true });
  }, [setSearchParams]);

  const backToTree = useCallback(() => {
    // Back to the tree, keeping the file's folder selected so the tree opens where the operator was
    const folder = selectedPath?.includes('/') ? selectedPath.slice(0, selectedPath.lastIndexOf('/')) : null;
    setSearchParams(folder ? { path: folder, type: 'directory' } : {}, { replace: true });
  }, [selectedPath, setSearchParams]);

  const refreshTree = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['workspace-tree', serverId] });
  }, [queryClient, serverId]);

  const refreshFile = useCallback(() => {
    if (selectedPath && selectedType === 'file') {
      queryClient.invalidateQueries({ queryKey: ['workspace-file', serverId, selectedPath] });
    }
  }, [queryClient, serverId, selectedPath, selectedType]);

  const refreshGit = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['workspace-git-status', serverId] });
    queryClient.invalidateQueries({ queryKey: ['workspace-git-branches', serverId] });
  }, [queryClient, serverId]);

  const filePath = selectedType === 'file' ? selectedPath : null;

  const tree = (
    <>
      <div className="flex-1 overflow-hidden">
        <FileTree serverId={serverId} selectedPath={selectedPath} onSelect={handleSelect} onRefresh={refreshTree} />
      </div>
      <GitPanel serverId={serverId} currentPath={selectedPath ?? undefined} onRefresh={refreshGit} />
    </>
  );

  if (!isDesktop) {
    return filePath ? (
      <div className={`flex ${height} flex-col`}>
        <button type="button" onClick={backToTree}
          className="mb-2 self-start rounded text-sm focus-visible:outline-2 focus-visible:outline-offset-2"
          style={{ color: 'var(--color-text-3)', outlineColor: 'var(--color-brand)' }} data-testid="back-to-tree"
          aria-label="Back to files">
          &larr; Files
        </button>
        <div className="flex flex-1 flex-col overflow-hidden rounded-lg" style={frame}>
          <FileViewer serverId={serverId} path={filePath} onRefresh={refreshFile} />
        </div>
      </div>
    ) : (
      <div className={`flex ${height} flex-col overflow-hidden rounded-lg`} style={frame}>{tree}</div>
    );
  }

  return (
    <div className={`flex ${height} overflow-hidden rounded-lg`} style={frame}>
      <div className="flex w-56 shrink-0 flex-col lg:w-72" style={{ borderRight: '1px solid var(--color-border)' }}>{tree}</div>
      <div className="flex-1 overflow-hidden">
        <FileViewer serverId={serverId} path={filePath} onRefresh={refreshFile} />
      </div>
    </div>
  );
}
